import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="toast"
export default class extends Controller {
  static values = {
    initialMessage: String,
    initialType: String,
    initialActionUrl: String,
    initialActionLabel: String,
    initialStorageKey: String
  }

  connect() {
    window.toastController = this
    this.initialNoticeTimer = window.setTimeout(() => this.showInitialNotice(), 700)
  }

  disconnect() {
    window.clearTimeout(this.initialNoticeTimer)
    if (window.toastController === this) {
      window.toastController = null
    }
  }

  showInitialNotice() {
    if (!this.hasInitialMessageValue) return
    if (this.hasInitialStorageKeyValue && this.initialNoticeShown(this.initialStorageKeyValue)) return

    this.show(
      this.initialMessageValue,
      this.hasInitialTypeValue ? this.initialTypeValue : 'warning',
      {
        actionUrl: this.hasInitialActionUrlValue ? this.initialActionUrlValue : null,
        actionLabel: this.hasInitialActionLabelValue ? this.initialActionLabelValue : null,
        duration: 15000
      }
    )

    if (this.hasInitialStorageKeyValue) this.markInitialNoticeShown(this.initialStorageKeyValue)
  }

  // Show a success toast notification
  success(message) {
    this.show(message, 'success')
  }

  // Show an error toast notification
  error(message) {
    this.show(message, 'error')
  }

  // Show a warning toast notification
  warning(message) {
    this.show(message, 'warning')
  }

  // Show an info toast notification
  info(message) {
    this.show(message, 'info')
  }

  // Main method to show toast
  show(message, type = 'info', options = {}) {
    // Remove any existing toasts
    this.removeAllToasts()

    // Get styles based on type
    const styles = this.getStylesForType(type)
    const icon = this.getIconForType(type)
    const duration = options.duration || this.getDurationForType(type)

    // Create toast element
    const toast = document.createElement('div')
    toast.className = `toast toast-${type}`
    toast.style.cssText = `
      background-color: ${styles.bg};
      border: 1px solid ${styles.border};
      border-left: 4px solid ${styles.accent};
      color: #1F2937;
      padding: 14px 16px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 12px;
      position: fixed;
      top: 24px;
      right: 24px;
      z-index: 9999;
      box-shadow: 0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.04);
      min-width: 320px;
      max-width: 420px;
      animation: toastSlideIn 0.35s cubic-bezier(0.21, 1.02, 0.73, 1);
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      overflow: hidden;
    `

    // Add copy button for error toasts
    const copyButton = type === 'error' ? `
      <button class="toast-copy-btn" style="
        background: none;
        border: none;
        color: #6B7280;
        cursor: pointer;
        padding: 4px;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 6px;
        transition: all 0.15s ease;
      " onmouseover="this.style.backgroundColor='#F3F4F6';this.style.color='#374151'" onmouseout="this.style.backgroundColor='transparent';this.style.color='#6B7280'" title="Copy error message">
        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
      </button>
    ` : ''

    toast.innerHTML = `
      <div style="
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background-color: ${styles.iconBg};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      ">
        ${icon}
      </div>
      <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start;">
        <span class="toast-message" style="font-size: 14px; font-weight: 500; color: #1F2937; word-break: break-word; overflow-wrap: break-word; line-height: 1.5;"></span>
        <span class="toast-action-slot"></span>
      </div>
      <div style="display: flex; align-items: center; gap: 2px; flex-shrink: 0;">
        ${copyButton}
        <button class="toast-close-btn" style="
          background: none;
          border: none;
          color: #9CA3AF;
          cursor: pointer;
          padding: 4px;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 6px;
          transition: all 0.15s ease;
        " onmouseover="this.style.backgroundColor='#F3F4F6';this.style.color='#374151'" onmouseout="this.style.backgroundColor='transparent';this.style.color='#9CA3AF'" title="Close">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div class="toast-progress" style="
        position: absolute;
        bottom: 0;
        left: 0;
        height: 3px;
        background-color: ${styles.accent};
        border-radius: 0 0 0 10px;
        width: 100%;
        animation: toastProgress ${duration}ms linear;
        opacity: 0.6;
      "></div>
    `

    const messageElement = toast.querySelector('.toast-message')
    messageElement.textContent = message

    if (options.actionUrl) {
      const action = document.createElement('a')
      action.href = options.actionUrl
      action.textContent = options.actionLabel || 'View details'
      action.style.cssText = `
        display: inline-flex;
        align-items: center;
        margin-top: 6px;
        color: ${styles.accent};
        font-size: 13px;
        font-weight: 700;
        line-height: 1.4;
        text-decoration: underline;
        text-underline-offset: 3px;
      `
      toast.querySelector('.toast-action-slot').appendChild(action)
    }

    // Add animation styles to document if not already added
    if (!document.querySelector('#toast-animations')) {
      const style = document.createElement('style')
      style.id = 'toast-animations'
      style.textContent = `
        @keyframes toastSlideIn {
          from {
            transform: translateX(calc(100% + 24px));
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes toastSlideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(calc(100% + 24px));
            opacity: 0;
          }
        }
        @keyframes toastProgress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `
      document.head.appendChild(style)
    }

    // Append to body
    document.body.appendChild(toast)

    // Add copy button event listener for error toasts
    if (type === 'error') {
      const copyBtn = toast.querySelector('.toast-copy-btn')
      if (copyBtn) {
        copyBtn.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(message)
            copyBtn.innerHTML = `
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
            `
            copyBtn.style.color = '#059669'
            setTimeout(() => {
              copyBtn.innerHTML = `
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              `
              copyBtn.style.color = '#6B7280'
            }, 1500)
          } catch (err) {
            console.error('Failed to copy text:', err)
          }
        })
      }
    }

    // Add close button event listener
    const closeBtn = toast.querySelector('.toast-close-btn')
    closeBtn.addEventListener('click', () => {
      this.dismissToast(toast)
    })

    // Auto-remove after duration with animation
    toast._dismissTimeout = setTimeout(() => {
      this.dismissToast(toast)
    }, duration)
  }

  initialNoticeShown(key) {
    try {
      return window.sessionStorage.getItem(key) === 'shown'
    } catch (_error) {
      return false
    }
  }

  markInitialNoticeShown(key) {
    try {
      window.sessionStorage.setItem(key, 'shown')
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }

  dismissToast(toast) {
    if (toast._dismissed) return
    toast._dismissed = true
    toast.style.animation = 'toastSlideOut 0.3s cubic-bezier(0.06, 0.71, 0.55, 1) forwards'
    setTimeout(() => toast.remove(), 300)
  }

  // Get color styles based on type
  getStylesForType(type) {
    const styles = {
      success: {
        accent: '#12B76A',
        bg: '#ECFDF3',
        border: '#A6F4C5',
        iconBg: '#D1FADF',
        iconColor: '#059669'
      },
      error: {
        accent: '#F04438',
        bg: '#FEF3F2',
        border: '#FECDCA',
        iconBg: '#FEE4E2',
        iconColor: '#DC2626'
      },
      warning: {
        accent: '#F79009',
        bg: '#FFFAEB',
        border: '#FEDF89',
        iconBg: '#FEF0C7',
        iconColor: '#D97706'
      },
      info: {
        accent: '#5A36E6',
        bg: '#F0EDFC',
        border: '#D6CCFA',
        iconBg: '#E8E2F8',
        iconColor: '#4427BF'
      }
    }
    return styles[type] || styles.info
  }

  // Get icon SVG based on type
  getIconForType(type) {
    const styles = this.getStylesForType(type)
    const icons = {
      success: `
        <svg width="18" height="18" fill="none" stroke="${styles.iconColor}" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
      `,
      error: `
        <svg width="18" height="18" fill="none" stroke="${styles.iconColor}" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      `,
      warning: `
        <svg width="18" height="18" fill="none" stroke="${styles.iconColor}" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        </svg>
      `,
      info: `
        <svg width="18" height="18" fill="none" stroke="${styles.iconColor}" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `
    }
    return icons[type] || icons.info
  }

  // Get auto-dismiss duration based on type (in milliseconds)
  getDurationForType(type) {
    const durations = {
      success: 5000,
      error: 15000,
      warning: 6000,
      info: 4000
    }
    return durations[type] || 4000
  }

  // Remove all existing toasts
  removeAllToasts() {
    const toasts = document.querySelectorAll('.toast')
    toasts.forEach((toast) => {
      window.clearTimeout(toast._dismissTimeout)
      toast.remove()
    })
  }
}
